import { type ComponentRef, forwardRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { useNavigate } from '@tanstack/react-router'

import { Button } from '@/components/atoms/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/Dialog'

import { Sheet, SheetContent, type SheetContentProps } from './Sheet'

export interface RouterSheetWithDirtyCheckProps extends SheetContentProps {
  to?: string
  isDirty?: boolean
}

export const RouterSheetWithDirtyCheck = forwardRef<ComponentRef<typeof SheetContent>, RouterSheetWithDirtyCheckProps>(
  ({ to = '..', isDirty = false, children, ...props }, ref) => {
    const navigate = useNavigate()
    const intl = useIntl()
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)

    const handleClose = async (value: boolean) => {
      if (!value) {
        if (isDirty) {
          setShowConfirmDialog(true)
        } else {
          await navigate({ to })
        }
      }
    }

    const handleConfirmLeave = async () => {
      setShowConfirmDialog(false)
      await navigate({ to })
    }

    return (
      <>
        <Sheet open onOpenChange={handleClose}>
          <SheetContent ref={ref} {...props}>
            {children}
          </SheetContent>
        </Sheet>

        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{intl.formatMessage({ id: 'form.unsavedChanges.title' })}</DialogTitle>
              <DialogDescription>{intl.formatMessage({ id: 'form.unsavedChanges.description' })}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="default"
                onClick={() => {
                  setShowConfirmDialog(false)
                }}>
                {intl.formatMessage({ id: 'form.unsavedChanges.stay' })}
              </Button>
              <Button type="button" variant="critical" onClick={handleConfirmLeave}>
                {intl.formatMessage({ id: 'form.unsavedChanges.leave' })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    )
  }
)
